import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { ChangeEvent } from '@ckeditor/ckeditor5-angular';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { ModalConfirmComponent } from 'src/app/components/system/modal-confirm/modal-confirm.component';
import { DataEntityAutocompleteComponent } from 'src/app/components/workflow/data-entity-autocomplete/data-entity-autocomplete.component';
import { Actions, Role, Workflow } from 'src/app/models';
import {
  Activity,
  ActivityModel,
  EmailActivityAttachment,
  EmailActivityModel
} from 'src/app/models/activities';
import { DataEntity } from 'src/app/models/data-entities';
import { EmailFooter } from 'src/app/models/email-footer';
import {
  SecurityService,
  TemplateService,
  Utilities,
  ValidationService,
  WorkflowContextService,
  WorkflowService
} from 'src/app/services';

@Component({
  selector: 'wm-email-editor',
  templateUrl: './email-editor.component.html',
  styleUrl: './email-editor.component.css',
  standalone: false
})
export class EmailEditorComponent implements OnInit, OnDestroy {
  @Input() activity: Activity<ActivityModel>; //changed this to a generic Activity

  // ------ added these inputs ------------
  @Input() emailModel: EmailActivityModel;
  @Input() workflow: Workflow;
  @Input() workflowVersionId: string;
  @Input() targetClientId: string;
  @Input() globalTarget = false;
  @Input() form: UntypedFormGroup;
  // --------------------------------------

  @Input() loadFull = false;
  @Input() viewInModal = true;
  @Input() showEmailFooterManagementLink = true;
  @Input() hideHeader = false;

  @ViewChild('emailBody', { static: false }) emailBody: any;
  @ViewChild('emailSubject', { static: false }) emailSubject: ElementRef;
  @ViewChild('emailRecipient', { static: false })
  emailRecipient: DataEntityAutocompleteComponent;
  @ViewChild('footerPreviewModal', { static: false })
  footerPreviewModal: ModalConfirmComponent;

  emailRegEx = /@\((.*)\)/i;
  entities: DataEntity[];
  filteredEntities: DataEntity[];
  attachmentEntities: EmailActivityAttachment[];
  filteredAttachmentEntities: EmailActivityAttachment[];
  emailDataEntities: DataEntity[];
  newEmailAddress: string;
  newEmailType: string;
  emailBodyField: AbstractControl;
  formGroupId: string;
  availableRoles: Role[];
  selectedRole: string;
  recipientDETypes: string[];
  excludedTemplateCodeSuffixes: string[];
  //form: UntypedFormGroup; // this was declared in email-activity-editor.  Not sure if that was ever needed since activityeditorbase had form, but now we need form as an input.
  activityForm: UntypedFormGroup;
  toEmailForm: UntypedFormGroup;
  attachForm: UntypedFormGroup;
  roleRecipientForm: UntypedFormGroup;
  isGlobal = false;
  refreshing = false;
  isCustomFromEnabled = false;
  allowReadReceipt = false;
  searchText: string;
  attachmentSearchText: string;

  public Editor = ClassicEditor;

  editorConfig = {
    toolbar: [
      'heading',
      '|',
      'bold',
      'italic',
      'link',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'undo',
      'redo'
      // 'image'
    ]
  };

  selectedFooterId: string;
  viewingFooter: EmailFooter = new EmailFooter();
  canManageEmailFooters = false;

  constructor(
    @Inject(forwardRef(() => TemplateService))
    public templateSvc: TemplateService,
    private _renderer: Renderer2,
    @Inject(forwardRef(() => WorkflowService))
    public workflowSvc: WorkflowService,
    @Inject(forwardRef(() => WorkflowContextService))
    private _context: WorkflowContextService,
    @Inject(forwardRef(() => SecurityService))
    private _securitySvc: SecurityService,
    private _fb: UntypedFormBuilder,
    private _ref: ChangeDetectorRef
  ) {
    this.toEmailForm = this._fb.group({});
    this.attachForm = this._fb.group({});
    this.roleRecipientForm = this._fb.group({});
    this.attachForm.setValidators([]);
  }

  cleanup() {
    this.loadFull = false;
    this.activityForm = null;

    this.form.removeControl('activityFormGroup');
  }

  showDetails(e) {
    this.loadFull = true;
    this.init();
  }

  recipientSpecified: ValidatorFn = (
    group: UntypedFormGroup
  ): ValidationErrors | null => {
    if (
      // it is a global workflow of any kind
      (this.workflow && !this.workflow.clientID) ||
      group.controls['applicant'].value ||
        false ||
        this.emailModel.to.length > 0 ||
        this.emailModel.cc.length > 0 ||
        this.emailModel.bcc.length > 0 ||
        this.emailModel.roles.length > 0
    ) {
      return null;
    } else {
      return { recipientSpecified: true };
    }
  };

  initEmail() {
    this.toEmailForm.addControl(
      'newEmailAddress',
      this._fb.control(
        '',
        Validators.compose([
          Validators.required,
          ValidationService.emailOrEntityValidator
        ])
      )
    );
    this.toEmailForm.addControl(
      'newEmailType',
      this._fb.control('to', Validators.required)
    );
    this.roleRecipientForm.addControl(
      'role',
      this._fb.control('', Validators.required)
    );

    this.toEmailForm.controls['newEmailAddress'].valueChanges.subscribe(
      changes => {
        // when an email is added, force the recipient component to re-load
        if (changes === 'RESET') {
          this.refreshing = true;
          setTimeout(() => {
            this.initEmail();
            this.refreshing = false;
          }, 1);
        }
      }
    );
  }

  init() {
    this.recipientDETypes = [
      WorkflowService.DATA_ENTITIES.FreeFormText.code,
      WorkflowService.DATA_ENTITIES.SystemData.code,
      WorkflowService.DATA_ENTITIES.ListData.code
    ];

    this.excludedTemplateCodeSuffixes = ['INSP_SDL_USER'];

    this.initEmail();

    const activityGroup = this._fb.group({
      subject: [this.emailModel.subject, Validators.required],
      body: [this.emailModel.body, Validators.required],
      applicant: [this.emailModel.emailApplicant, Validators.nullValidator],
      requestReadReceipt: [
        this.emailModel.requestReadReceipt,
        Validators.nullValidator
      ],
      customFromAddress: [
        this.emailModel.customFromAddress,
        Validators.nullValidator
      ]
    });

    this.activityForm = activityGroup;

    this.activityForm.controls['requestReadReceipt'].valueChanges.subscribe(
      e => {
        if (e) {
          this.activityForm.controls['customFromAddress'].setValidators([
            Validators.required
          ]);
          this.activityForm.controls[
            'customFromAddress'
          ].updateValueAndValidity();
          this.activityForm.controls['customFromAddress'].markAsTouched();
        } else {
          this.activityForm.controls['customFromAddress'].setValidators([
            Validators.nullValidator
          ]);
          this.activityForm.controls[
            'customFromAddress'
          ].updateValueAndValidity();
        }
      }
    );

    this.emailBodyField = this.activityForm.controls['body'];

    this.activityForm.setValidators([this.recipientSpecified.bind(this)]);

    const that = this;

    this.workflowSvc
      .getDataEntitiesBeforeMe(
        this.workflow,
        this.activity,
        WorkflowService.getEmailDataEntityTypes(),
        null,
        null,
        this.workflowVersionId
      )
      .subscribe(entities => {
        this.entities = entities;
        this.filteredEntities = Object.assign([], this.entities);
      });

    this.workflowSvc
      .getEmailDataEntities(
        this.workflow,
        this.activity,
        this.workflowVersionId
      )
      .subscribe(entities => {
        this.emailDataEntities = entities;
      });

    this.buildAvailableAttachments();

    this._securitySvc
      .getRoles(this.targetClientId || this._context.client?.id)
      .subscribe(roles => {
        this.availableRoles = roles;
      });

    this.selectedFooterId = this.emailModel.emailFooterId || null;

    this.form.addControl('activityFormGroup', this.activityForm);
  }

  ngOnInit() {
    this._securitySvc
      .isLoginEntitled(Actions.MANAGE_EMAIL_FOOTERS)
      .subscribe(result => {
        this.canManageEmailFooters = result;
      });

    if (!this.workflowVersionId || this.globalTarget) {
      this.workflow = this.workflow || this._context.workflow;
      this.isGlobal = !this.workflow?.clientID || this.globalTarget;
    }

    this.isCustomFromEnabled = this.emailModel.allowCustomFromAddress;
    this.allowReadReceipt = this.isCustomFromEnabled;

    if (this.loadFull) {
      this.init();
    }
  }

  ngOnDestroy(): void {
    this.loadFull = false;
  }

  insertSubject(de: DataEntity) {
    Utilities.insertValue(
      this.emailSubject.nativeElement,
      this._renderer,
      this.templateSvc.generateToken(de),
      this.activityForm.controls['subject']
    );
  }

  onChange({ editor }: ChangeEvent) {
    const data = editor.data.get();
    this.emailModel.body = data;
  }

  insertRecipient(de: DataEntity) {
    this.emailRecipient.insertDataEntity(de.templateCode);
  }

  insertBody(de: DataEntity) {
    const editor = this.emailBody.editorInstance;

    editor.model.change(writer => {
      const insertPosition = editor.model.document.selection.getFirstPosition();
      writer.insertText(this.templateSvc.generateToken(de), insertPosition);
    });
  }

  buildAvailableAttachments() {
    this.workflowSvc
      .getEmailAttachments(this.workflow, this.activity, this.workflowVersionId)
      .subscribe(attachments => {
        const availAttachments = [];

        attachments.forEach((a, i) => {
          const exAttachment = this.emailModel.attachments.find(
            exA =>
              exA.activityId === a.activityId &&
              (exA.deTemplateCode || '') === (a.deTemplateCode || '')
          );
          if (!exAttachment) {
            availAttachments.push(a);
          }
        });
        this.attachmentEntities = availAttachments;
        this.filteredAttachmentEntities = Object.assign(
          [],
          this.attachmentEntities
        );
      });
  }

  selectImage() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.click();

    // Listen upload local image and save to server
    input.onchange = () => {
      const file = input.files[0];

      // file type is only image.
      if (/^image\//.test(file.type)) {
        this.uploadImage(file);
      } else {
        console.warn('You can only upload images.');
      }
    };
  }

  uploadImage(file: File) {
    // TODO: Implement upload
    this.insertImage(file.name);
  }

  insertImage(url) {}

  attachDoc(de: EmailActivityAttachment) {
    if (!this.emailModel.attachments) {
      this.emailModel.attachments = [];
    }

    this.emailModel.attachments.push(de);

    this.attachmentEntities.splice(this.attachmentEntities.indexOf(de), 1);

    this.filteredAttachmentEntities.splice(
      this.attachmentEntities.indexOf(de),
      1
    );

    this.attachForm.updateValueAndValidity();
  }

  removeAttachment(de: EmailActivityAttachment) {
    this.emailModel.attachments.splice(
      this.emailModel.attachments.indexOf(de),
      1
    );
    this.buildAvailableAttachments();
  }

  addEmail() {
    const type = this.toEmailForm.controls['newEmailType'];
    const email = this.toEmailForm.controls['newEmailAddress'];

    const swappedEntityEmail = email.value.replace(this.emailRegEx, '${$1}');

    switch (type.value) {
      case 'bcc':
        this.addBCCAddress(swappedEntityEmail);
        break;
      case 'cc':
        this.addCCAddress(swappedEntityEmail);
        break;
      case 'to':
        this.addToAddress(swappedEntityEmail);
        break;
      default:
        alert(swappedEntityEmail);
        break;
    }

    email.setValue('RESET');
    type.setValue('to');

    this._ref.detectChanges();
    email.markAsUntouched();
    this.form.updateValueAndValidity();
    this.activityForm.updateValueAndValidity();
  }

  addCCAddress(emailAddress: string) {
    if (!this.emailModel.cc) {
      this.emailModel.cc = [];
    }

    if (!this.emailModel.cc.includes(emailAddress)) {
      this.emailModel.cc.push(emailAddress);
    }
  }

  addBCCAddress(emailAddress: string) {
    if (!this.emailModel.bcc) {
      this.emailModel.bcc = [];
    }

    if (!this.emailModel.bcc.includes(emailAddress)) {
      this.emailModel.bcc.push(emailAddress);
    }
  }

  addToAddress(emailAddress: string) {
    if (!this.emailModel.to) {
      this.emailModel.to = [];
    }

    if (!this.emailModel.to.includes(emailAddress)) {
      this.emailModel.to.push(emailAddress);
    }
  }
  addRole() {
    if (!this.emailModel.roles) {
      this.emailModel.roles = [];
    }

    const role = this.availableRoles.find(r => r.id === this.selectedRole);

    if (role) {
      if (!this.emailModel.roles.includes(role)) {
        this.emailModel.roles.push(role);
      }
      this.selectedRole = null;
    }

    this.activityForm.updateValueAndValidity();
  }
  removeToAddress(emailAddress) {
    this.emailModel.to.splice(this.emailModel.to.indexOf(emailAddress), 1);
    this.form.updateValueAndValidity();
    this.activityForm.updateValueAndValidity();
  }
  removeCCAddress(emailAddress) {
    this.emailModel.cc.splice(this.emailModel.cc.indexOf(emailAddress), 1);
    this.form.updateValueAndValidity();
    this.activityForm.updateValueAndValidity();
  }
  removeBCCAddress(emailAddress) {
    this.emailModel.bcc.splice(this.emailModel.bcc.indexOf(emailAddress), 1);
    this.form.updateValueAndValidity();
    this.activityForm.updateValueAndValidity();
  }
  removeRole(role) {
    this.emailModel.roles.splice(this.emailModel.roles.indexOf(role), 1);
  }

  searchDataEntities() {
    if (this.searchText) {
      this.filteredEntities = this.entities.filter(de =>
        de.templateCode.toLowerCase().includes(this.searchText.toLowerCase())
      );
    } else {
      this.filteredEntities = Object.assign([], this.entities);
    }
  }

  searchAttachmentDataEntities() {
    if (this.attachmentSearchText) {
      this.filteredAttachmentEntities = this.attachmentEntities.filter(ae =>
        ae.name.toLowerCase().includes(this.attachmentSearchText.toLowerCase())
      );
    } else {
      this.filteredAttachmentEntities = Object.assign(
        [],
        this.attachmentEntities
      );
    }
  }

  onFooterChanged() {
    this.emailModel.emailFooterId = this.selectedFooterId;
  }

  viewFooter() {
    this.viewingFooter = this.emailModel.availableFooters.find(
      as => as.id === this.selectedFooterId
    );

    this.footerPreviewModal.open();
  }
}
